import { useState, useRef, useEffect, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadingSavedPrescriptions } from "../../Redux/Slice/SavedPrescriptionSlice";
import {
  Box,
} from "@mui/material";
import { motion } from "framer-motion";
import { ErrorBoundary } from "react-error-boundary";
import { primaryColor } from "../../Theme";
import SearchInputSection from "../../components/AIPrescription/SearchInputSection";
import AgenticRxQuickGuide from "../../components/AIPrescription/AgenticRxQuickGuide";
import AgenticRxFeatureAccess from "../../components/AIPrescription/AgenticRxFeatureAccess";
import ConversationCard from "../../components/AIPrescription/ConversationCard/index";
import TabsNav from "../../components/TabsNav";
import { setAgenticDraftSaver } from "../../utils/AIPrescription/agenticDraftSave";
import { parseAIPrescriptionText, AIPrescriptionPayload } from "../../Apis/AIPrescriptionApis";
import {
  createEmptyMedicationData,
  ConversationItem,
  SearchSession,
  updatePharmacyFromWebSocket,
  updateConversationById as updateConversationByIdUtil,
  ensureHeldStateForPatient,
  getHeldConversations,
  getHeldShowResults,
  subscribeConversationState,
  updateHeldConversations,
  setHeldShowResults,
  setSessionForSearch,
  handleVoiceResult as handleVoiceResultUtil,
  handlePrescriptionAPI,
  scrollToLatest,
  scrollLatestBottomIntoView,
  TIMEOUTS,
  normalizeQuery,
  getInitialPharmacyState,
  clearConversationState,
  clearSearchHistory,
} from "../../utils/AIPrescription";
import { resolveIsAgentixRxEnabled } from "../../utils/AIPrescription/agentixRxAccess";
import { useWebSocketMessageHandler } from "../../hooks/useWebSocketMessageHandler";
import { createHandlers } from "./handlers";
import { executeSearch } from "./searchFlow";
import { getResultsContainerSx, pageContainerStyle, resultsSectionStyle } from "./styles";
import { useViewportLock } from "./useViewportLock";
import { setPatientContext, getPatientId } from "../../utils/patientSession";
import {
  buildUpdateClauseForPrescription,
  clearPrescriptionEditsForMedication,
  wasEditClauseConsumed,
  markEditClauseConsumed,
  clearPrescriptionEdits,
} from "../../utils/AIPrescription/prescriptionEditsStore";
import {
  markSendOriginButton,
  clearPendingSendOrigin,
} from "../../utils/AIPrescription/sendOriginStore";
import { clearPrescriptionFlags } from "../../utils/AIPrescription/prescriptionFlagsStore";
import { clearSessionBenefitSource } from "../../utils/AIPrescription/sessionBenefitSourceStore";
import { setStoredOrchestrationSessionId } from "../../Apis/AIPrescriptionApis/orchestrationSession";
import { clearOrchestrationConversation } from "../../utils/authCookie";
import { getTextWebSocketManager } from "../../Apis/AIPrescriptionApis/websocketManager";

/** Shown as user query and sent as `text` when search is submitted with a VTT file but no typed message */
const VTT_ONLY_SUBMIT_TEXT = "Transcript loaded from VTT file";

export default function AIPrescription() {
  const location = useLocation();
  const dispatch = useDispatch();
  const profileGroup = useSelector(
    (s: any) => s?.UserServicesSlice?.ServicesData?.profileGroup
  );
  const isAgentixRxEnabled = useSelector((s: any) =>
    resolveIsAgentixRxEnabled(s?.UserServicesSlice?.ServicesData)
  );
  const prescriberDetails = useSelector(
    (s: any) => s?.PrescriberDetailsSlice?.AllPrescriberDetails
  );
  // Input bar open/closed (toggled by the agent bubble). When closed, the
  // results/summary area reclaims the freed space.
  const [barOpen, setBarOpen] = useState(true);
  // Reconcile the held conversation store with the active patient BEFORE the first
  // read below (a different patient than the one currently held resets to a fresh
  // conversation so PHI never leaks across patients). Runs once per mount, before
  // paint, via the lazy useState initializer trick.
  useState(() => {
    ensureHeldStateForPatient(getPatientId());
    return null;
  });
  // `conversations` and `showResults` are backed by a module-level store (not local
  // component state) so the in-progress conversation survives this component
  // unmounting when the user switches to another tab (Medication History, Favourite
  // Drugs, etc.) mid-request. The WebSocket message handler writes directly into
  // that store, so an agent turn that finishes while the user is elsewhere is never
  // lost — it's just waiting here for the next time this page renders.
  const conversations = useSyncExternalStore(subscribeConversationState, getHeldConversations);
  const setConversations = updateHeldConversations;
  const showResults = useSyncExternalStore(subscribeConversationState, getHeldShowResults);
  const setShowResults = setHeldShowResults;
  const [searchHistory, setSearchHistory] = useState<SearchSession[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [voiceSearchCountdown, setVoiceSearchCountdown] = useState<number | null>(null);
  // Load preferredPharmacy from localStorage on mount
  // NOTE: We clear localStorage on mount and unmount to ensure fresh start
  // Pharmacy will be populated from WebSocket result messages
  const [preferredPharmacy, setPreferredPharmacy] = useState<string>(() =>
    getInitialPharmacyState().preferredPharmacy
  );
  const [pharmacyIdFromAPI, setPharmacyIdFromAPI] = useState<string | null>(() =>
    getInitialPharmacyState().pharmacyIdFromAPI
  );
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const latestConversationRef = useRef<HTMLDivElement | null>(null);
  const voiceSearchTimerRef = useRef<NodeJS.Timeout | null>(null);
  const voiceCountdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const formikRef = useRef<any>(null);
  const resultMessageProcessedRef = useRef<boolean>(false);
  const { sentinelRef, lockStyle } = useViewportLock();
  const [medicationPicksByName, setMedicationPicksByName] = useState<
    Record<string, string>
  >({});
  const [pharmacyPicksByName, setPharmacyPicksByName] = useState<
    Record<string, string>
  >({});
  const apiCallTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSeenPatientIdRef = useRef<string>('');

  // Pharmacy is populated from WebSocket result messages only

  // Persist patient context from navigation (e.g. Patient Search → AI Prescription) in case
  // session was cleared; keeps patientName in sync for orchestration WebSocket payloads.
  // Also validates the held conversation against the new patient context to prevent PHI leakage.
  useEffect(() => {
    const st = location.state;
    if (!st || typeof st !== "object") {
      return;
    }
    const name = (st as { patientName?: string }).patientName;
    const pid = (st as { personId?: string }).personId;
    const hostId = (st as { hostPatientId?: string }).hostPatientId;
    setPatientContext({
      patientName: name,
      patientId: pid,
      hostPatientId: hostId,
    });
    // Validate that the held conversation matches the patient we just navigated to.
    // If a different patient was navigated to (e.g. from Patient Search), reset the conversation.
    // If the same patient, keep the held state.
    if (pid) {
      ensureHeldStateForPatient(pid);
    }
  }, [location.state]);

  // Monitor patient ID changes from any source (navigation, dropdown selector, etc).
  // This catches patient switches that don't trigger location.state changes (e.g., a patient
  // selector dropdown within the same page). Without this, switching Patient A → B via dropdown
  // would update patientContext but never call ensureHeldStateForPatient, leaving Patient A's
  // conversation in the module-level held state and causing data leakage when patient B's page
  // later triggers a resumed conversation from the backend (which still had Patient A's state).
  useEffect(() => {
    const currentPatientId = getPatientId();
    // Detect changes in patient ID. This works both when:
    // 1. Patient ID was previously set and is now different
    // 2. Patient ID is being set for the first time (from empty to a value)
    const patientChanged = currentPatientId && currentPatientId !== lastSeenPatientIdRef.current;
    if (patientChanged) {
      lastSeenPatientIdRef.current = currentPatientId;
      ensureHeldStateForPatient(currentPatientId);
    }
  });

  // Sync preferredPharmacy (from WSS) into prescriptions when it or conversations change
  useEffect(() => {
    const defaultPharmacy = preferredPharmacy || "";
    if (!defaultPharmacy || conversations.length === 0) return;
    const hasEmptyPharmacy = conversations.some((conv) =>
      conv.prescriptions.some((pres) => !pres.pharmacy || pres.pharmacy === "")
    );
    if (!hasEmptyPharmacy) return;
    setConversations((prev) =>
      prev.map((conv) => ({
        ...conv,
        prescriptions: conv.prescriptions.map((pres) => {
          if (!pres.pharmacy || pres.pharmacy === "") {
            return { ...pres, pharmacy: defaultPharmacy };
          }
          return pres;
        })
      }))
    );
  }, [preferredPharmacy, conversations]);

  // Helper function to update pharmacy from WebSocket data (wrapper for utility)
  const updatePharmacyFromWebSocketWrapper = (data: any) => {
    updatePharmacyFromWebSocket(data, setPreferredPharmacy, setPharmacyIdFromAPI);
  };

  // Helper function to update a conversation by ID (wrapper for utility)
  const updateConversationById = (
    conversationId: number,
    updates: Partial<ConversationItem> | ((conv: ConversationItem) => ConversationItem),
    logMessage?: string
  ) => {
    setConversations((prev) => updateConversationByIdUtil(prev, conversationId, updates, logMessage));
  };

  // Reusable function to handle parseAIPrescriptionText API calls (wrapper for utility)
  const handlePrescriptionAPIWrapper = (
    payload: AIPrescriptionPayload,
    conversationId: number,
    options: {
      logContext: string;
      structuredData: any[];
      lastConversation: ConversationItem;
      onMedicationData?: (response: any, medicationDataArray: any[], repromptMessage: string) => {
        prescriptions: any[];
        aiResponse: string;
        originalPrescription?: any;
        structuredMedicationData?: any[];
        showPharmacyList?: boolean;
      } | null;
      onSuccess?: (response: any, updatedStructuredData: any[], repromptMessage: string) => {
        prescriptions: any[];
        aiResponse: string;
        originalPrescription?: any;
      };
      onError?: () => {
        prescriptions: any[];
        aiResponse: string;
        originalPrescription?: any;
      };
    }
  ) => {
    handlePrescriptionAPI(
      payload,
      conversationId,
      options,
      {
        updatePharmacy: updatePharmacyFromWebSocketWrapper,
        updateConversation: (id, updates) => updateConversationById(id, updates),
        setIsSearching,
        isMounted: () => isMountedRef.current,
        resultMessageProcessedRef,
      }
    );
  };

  // Use WebSocket message handler hook
  useWebSocketMessageHandler({
    isMountedRef,
    resultMessageProcessedRef,
    setConversations,
    setIsSearching,
    preferredPharmacy,
    updatePharmacy: updatePharmacyFromWebSocketWrapper,
  });


  // Function to scroll container to show latest conversation from its beginning with smooth animation
  const scrollToLatestWrapper = () => {
    scrollToLatest(resultsContainerRef, latestConversationRef, scrollTimeoutRef);
  };

  // Scroll to show latest conversation from its beginning when new conversations are added
  useEffect(() => {
    if (conversations.length > 0) {
      const timeoutId = setTimeout(() => {
        scrollToLatestWrapper();
      }, TIMEOUTS.SCROLL_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [conversations.length]);

  // Follow live status checklist growth so each new line stays in view.
  const latestStatusCount =
    conversations.length > 0
      ? conversations[conversations.length - 1].statusMessages?.length ?? 0
      : 0;
  useEffect(() => {
    if (latestStatusCount === 0) return;
    scrollLatestBottomIntoView(
      resultsContainerRef,
      latestConversationRef,
      scrollTimeoutRef
    );
  }, [latestStatusCount]);

  // Scroll to show latest conversation from its beginning when search finishes loading
  const prevIsSearchingRef = useRef<boolean>(false);
  useEffect(() => {
    if (prevIsSearchingRef.current === true && isSearching === false) {
      const timeoutId = setTimeout(() => {
        scrollToLatestWrapper();
      }, TIMEOUTS.SCROLL_DELAY_AFTER_SEARCH);

      return () => clearTimeout(timeoutId);
    }
    prevIsSearchingRef.current = isSearching;
  }, [isSearching]);

  // Search history management - wrapper for utility
  const setSessionForSearchWrapper = (query: string) => {
    const updatedHistory = setSessionForSearch(query);
    setSearchHistory(updatedHistory);
    return updatedHistory;
  };

  // `conversations` is an append-only chat history: each turn's `prescriptions` is a
  // frozen snapshot from when that turn was rendered, and older turns are never updated
  // in place (e.g. their `sent` flag never flips retroactively once the drug is actually
  // sent, and their dosage/SIG snapshot never refreshes once a later turn updates it).
  // Any consumer that needs "what does this medication currently look like" must resolve
  // to the LATEST occurrence per medication name first — never scan every historical
  // occurrence, or a stale pre-update/pre-send snapshot will keep looking unsent/edited
  // forever and re-trigger for every medication turn after turn.
  const getLatestPrescriptionsByMedication = (): any[] => {
    const latestByMedication = new Map<string, any>();
    conversations.forEach((c) => {
      (Array.isArray(c.prescriptions) ? c.prescriptions : []).forEach((p: any) => {
        const name = (p?.medication_name || p?.drugName || "").trim().toLowerCase();
        if (!name) return;
        // Later conversations are appended later (see setConversations push in
        // handleSearch), so iterating in order and always overwriting naturally
        // leaves the most recent snapshot per medication.
        latestByMedication.set(name, p);
      });
    });
    return Array.from(latestByMedication.values());
  };

  const getLatestUnsentPrescriptions = (): any[] =>
    getLatestPrescriptionsByMedication().filter((p: any) => !p?.sent);

  // Card edits (pencil icon) on an unsent draft otherwise only reach the backend via
  // that card's own Send button. If the user instead types an unrelated chat message
  // (e.g. "change pharmacy to brooklyn") while a pending edit (e.g. a dosage change)
  // sits unsaved on another/the same card, the edit would be silently dropped. Prepend
  // an update clause per edited, unsent draft — same helper/phrasing the Send button and
  // the draft-saver use — so the submitted (and displayed) message carries both.
  //
  // `query` (when the caller has one — the draft-saver doesn't) lets us skip a
  // medication the user already named in their own typed instruction. Without this,
  // typing "Update CeleXA: strength to 200 MG. save the clarithromycin prescription"
  // while that same CeleXA edit is still pending got the clause prepended a second
  // time, doubling "update CeleXA..." in the outgoing (and displayed) message.
  //
  // wasEditClauseConsumed/markEditClauseConsumed guard against a SEPARATE re-appearance bug:
  // once a clause has ridden along in an outgoing message, PrescriptionSummary's own mirroring
  // effect (its card instance stays mounted and keeps re-writing local editState back into the
  // ambient store on every re-render) can resurrect the exact same edit in the store right
  // after we clear it here. Clearing the store is still done as best-effort cleanup (helps
  // once that card unmounts), but the actual "already sent, don't resend" gate has to be
  // module-level, keyed by the literal clause text — a component-local ref here gets wiped on
  // every ordinary tab-switch remount of this page, while the ambient store and the mirroring
  // effect that resurrects the edit both survive that same remount (see that store's
  // docstring), so a ref-based gate would forget "already sent" the moment the user switched
  // tabs and back, and silently resend a stale edit clause on the next unrelated message.
  const collectPendingUpdateClauses = (query: string = ""): string[] => {
    const patientId = getPatientId();
    const queryLower = query.toLowerCase();
    const allPrescriptions = getLatestUnsentPrescriptions();

    // CRITICAL: Handle medications mentioned in the query (filter-out path)
    // Even though we DON'T prepend a clause for medications already mentioned by the user,
    // we MUST mark their edits as consumed to prevent them from being prepended later
    // when the user sends an unrelated message (e.g., mentioned Lisinopril in turn 2,
    // then send Abilify in turn 5 — Lisinopril edit must not resurface).
    allPrescriptions.forEach((p: any) => {
      const name = (p?.medication_name || p?.drugName || "").trim().toLowerCase();
      if (name && queryLower.includes(name)) {
        // This medication is mentioned in the current query
        const normName = (p?.medication_name || p?.drugName || "").trim();
        const clause = buildUpdateClauseForPrescription(p, patientId, preferredPharmacy);
        if (clause && normName) {
          // Mark this exact clause as consumed so it won't be prepended later
          markEditClauseConsumed(patientId, normName, clause);
          // Also clear the edit from the store to ensure it can't be resurrected
          clearPrescriptionEditsForMedication(patientId, normName);
        }
      }
    });

    // Filter: process only medications NOT mentioned in the query (prepend path)
    return allPrescriptions
      .filter((p: any) => {
        const name = (p?.medication_name || p?.drugName || "").trim().toLowerCase();
        return !(name && queryLower.includes(name));
      })
      .map((p: any) => {
        const clause = buildUpdateClauseForPrescription(p, patientId, preferredPharmacy);
        if (!clause) return "";
        const name = (p?.medication_name || p?.drugName || "").trim();
        if (!name) return clause;
        if (wasEditClauseConsumed(patientId, name, clause)) {
          // Identical to what we already sent for this medication — already consumed.
          return "";
        }
        markEditClauseConsumed(patientId, name, clause);
        clearPrescriptionEditsForMedication(patientId, name);
        return clause;
      })
      .filter((clause): clause is string => !!clause);
  };

  const mergeWithPendingEdits = (query: string): string => {
    const updateClauses = collectPendingUpdateClauses(query);
    return updateClauses.length > 0 ? `${updateClauses.join(". ")}. ${query}` : query;
  };

  // Resets the chat/draft on both sides without ending the login or deselecting the
  // patient — distinct from Logout (LogoutSaga), which also clears these plus the auth
  // session itself and reloads the page. Guarded by `disabled={isSearching}` on the
  // SearchInputSection link below, same as the input bar, so an in-flight turn's response
  // can't land into a just-cleared conversation.
  //
  // Critical: backend clear must complete BEFORE frontend removes sessionId. If the
  // sessionId is removed while the clear request is still in flight, a reconnection that
  // happens before the backend processes the clear could reuse the old session. The order:
  // 1. Clear local conversation UI state (immediate)
  // 2. Await backend to clear its session (wait for confirmation)
  // 3. Remove sessionId from localStorage (only after backend confirms)
  // 4. Close WebSocket (already cleared on backend, safe to close)
  const handleClearConversation = async () => {
    // Step 1: Clear local conversation UI state (including draft input)
    clearConversationState();
    setSearchHistory([]);
    setVoiceSearchCountdown(null);
    setMedicationPicksByName({});
    setPharmacyPicksByName({});
    clearSearchHistory();
    clearPrescriptionFlags();
    clearSessionBenefitSource();
    clearPrescriptionEdits();
    clearPendingSendOrigin();

    // Also clear the Formik input field
    if (formikRef.current) {
      formikRef.current.setFieldValue('prescriptionText', '');
    }

    // Step 2: Await backend to clear session (conversation_history, medication_data, agent.context)
    // This MUST complete before we clear the sessionId, otherwise a race condition could
    // allow a reconnection to restore the old session before the backend clears it.
    await clearOrchestrationConversation();

    // Step 3: Only after backend confirms, remove sessionId from localStorage
    // This ensures the next WebSocket connection will have no sessionId in the URL,
    // forcing the backend to generate a new one.
    setStoredOrchestrationSessionId(null);

    // Step 4: Close the WebSocket (it's already cleared on the backend)
    getTextWebSocketManager().close();
  };

  // Wraps handleSearch for the typed/VTT search box specifically (SearchInputSection's
  // `onSearch` below) — NOT for handleSearch's other callers (card Send/Send-all, the
  // draft-saver) since those already build their own update clauses into the message they
  // pass in; merging again here would duplicate them.
  const handleTypedSearch = (
    query: string,
    options?: { vttContent?: string; immediate?: boolean }
  ) => {
    // A VTT-only submission (no typed text) has nothing to merge into — let handleSearch's
    // own VTT_ONLY_SUBMIT_TEXT fallback apply instead of prepending onto an empty string.
    const merged = query.trim() ? mergeWithPendingEdits(query) : query;
    handleSearch(merged, options);
  };

  const handleSearch = async (
    query: string,
    options?: { vttContent?: string; immediate?: boolean }
  ) => {
    const vtt = options?.vttContent?.trim();
    if (!query.trim() && !vtt) {
      return;
    }
    // Single choke point for every submission path (chat Enter/Send, the voice
    // auto-search timer in voiceSearchUtils.ts, and the summary card's Send/Send
    // all) — a turn already in flight blocks a second one here regardless of
    // whether the UI surface that triggered it remembered to disable itself.
    // Critical for Send/Send all, which map to send2Pharmacy: this is what
    // actually prevents a duplicate prescription submission, not just a UI nicety.
    if (isSearching) {
      return;
    }

    if (voiceSearchTimerRef.current) {
      clearTimeout(voiceSearchTimerRef.current);
      voiceSearchTimerRef.current = null;
    }
    if (voiceCountdownIntervalRef.current) {
      clearInterval(voiceCountdownIntervalRef.current);
      voiceCountdownIntervalRef.current = null;
    }
    setVoiceSearchCountdown(null);

    const queryForPayload = query.trim() ? query : VTT_ONLY_SUBMIT_TEXT;
    const normalizedQuery = normalizeQuery(queryForPayload);
    setIsSearching(true);
    setShowResults(true);
    setSessionForSearchWrapper(normalizedQuery);

    const conversationId = Date.now();
    const searchText = normalizedQuery.toLowerCase();
    const lastConversation = conversations.length > 0 ? conversations[conversations.length - 1] : null;

    resultMessageProcessedRef.current = false;

    const initialConversation: ConversationItem = {
      id: conversationId,
      userQuery: normalizedQuery,
      aiResponse: "",
      isLoading: true,
      prescriptions: [],
      timestamp: new Date(),
      structuredMedicationData: createEmptyMedicationData(),
    };

    setConversations((prev) => [...prev, initialConversation]);

    executeSearch({
      normalizedQuery,
      query,
      conversations,
      conversationId,
      searchText,
      lastConversation,
      preferredPharmacy,
      vttContent: vtt,
      resultMessageProcessedRef,
      apiCallTimeoutRef,
      isMountedRef,
      setConversations,
      setIsSearching,
      handlePrescriptionAPIWrapper,
      immediate: options?.immediate,
    });
  };

  // Send / Send all on the prescription summary: submit the chat message that the
  // orchestration agent's send recognizer understands (single drug -> scoped send,
  // "send all prescriptions to pharmacy" -> batch send of every ready prescription).
  // Runs immediately (bypasses the shared search debounce) — otherwise sending one edited
  // drug and then quickly sending another cancels the first send before it reaches the API,
  // since both would share the same apiCallTimeoutRef.
  //
  // Merges in every OTHER unsent draft's pending edits first — same as the typed chat
  // box (handleTypedSearch) — so clicking Send/Send all on one card doesn't silently drop
  // an edit still sitting unsaved on a sibling card or an earlier conversation's draft.
  // mergeWithPendingEdits already skips any drug the message itself names, so the
  // clicked card's own update-then-send clause is never duplicated.
  const handleSendPrescription = (message: string) => {
    const merged = mergeWithPendingEdits(message);
    // Re-stage: the button's onClick marks the origin against ITS OWN message, but
    // mergeWithPendingEdits can prepend sibling-card update clauses, changing the string
    // createPayload will actually see. Re-mark against the merged text so the marker still
    // matches at consume time.
    markSendOriginButton(merged);
    handleSearch(merged, { immediate: true });
  };

  // True when there is at least one ready, not-yet-sent Agentic draft worth
  // persisting to Saved Prescriptions before the user leaves the pad. Uses the same
  // latest-per-medication resolution as collectPendingUpdateClauses so a stale historical
  // snapshot (already sent/updated in a later turn) can't falsely keep this true forever.
  const hasUnsentReadyDrafts = () => getLatestUnsentPrescriptions().length > 0;

  const handleVoiceResult = async (text: string) => {
    handleVoiceResultUtil(
      text,
      formikRef,
      voiceSearchTimerRef,
      voiceCountdownIntervalRef,
      setVoiceSearchCountdown,
      handleSearch
    );
  };

  // Create event handlers using the handlers utility
  const handlers = createHandlers({
    conversations,
    setConversations,
    setPreferredPharmacy,
    setPharmacyIdFromAPI,
    formikRef,
    resultsContainerRef,
    latestConversationRef,
    scrollTimeoutRef,
    medicationPicksByName,
    setMedicationPicksByName,
    pharmacyPicksByName,
    setPharmacyPicksByName,
    onAutoSend: handleSearch,
  });

  const {
    handleSuggestionSelect,
    handlePharmacyLookupSelect,
    handlePbmSelect,
    handleFavouriteDrugSelect,
    handleFeedback,
    handlePharmacySelection,
  } = handlers;

  // Populate the Saved Prescriptions count so the red "*" indicator on that tab
  // shows while in the Agentic pad too (TabsNav reads the SavedPrescription slice).
  useEffect(() => {
    const patientId = sessionStorage.getItem("patientId");
    if (!patientId) return;
    dispatch(
      loadingSavedPrescriptions({
        pageNumber: 0,
        patientId,
        doctorId:
          profileGroup > 6
            ? localStorage.getItem("SupervisorDoctorId")
            : localStorage.getItem("doctorId"),
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileGroup]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (voiceSearchTimerRef.current) {
        clearTimeout(voiceSearchTimerRef.current);
      }
      if (voiceCountdownIntervalRef.current) {
        clearInterval(voiceCountdownIntervalRef.current);
      }
      if (apiCallTimeoutRef.current) {
        clearTimeout(apiCallTimeoutRef.current);
      }
    };
  }, []);

  // Register the draft-saver so switching to New Rx Pad — via the PadSwitcher
  // dropdown OR the "New Rx Pad" tab in TabsNav — auto-saves current drafts to
  // Saved Prescriptions. Re-registered on conversation changes to avoid stale
  // reads; cleared on unmount so it never fires from other pages.
  useEffect(() => {
    setAgenticDraftSaver(() => {
      if (!hasUnsentReadyDrafts()) return;
      // Card edits (pencil icon) only ever reached the backend via the single-drug
      // Send button's own message; the Save trigger previously re-saved whatever
      // medication data the backend already had, silently dropping edits. Prepend
      // an update clause per edited, unsent draft so Save applies the latest values.
      const updateClauses = collectPendingUpdateClauses();

      const saveMessage =
        updateClauses.length > 0
          ? `${updateClauses.join(". ")}. Then save all prescriptions`
          : "save all prescriptions";
      handleSearch(saveMessage);
    });
    return () => setAgenticDraftSaver(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, preferredPharmacy]);



  return (
    <>
      {/* In-flow anchor for column width / top offset; panel itself is fixed. */}
      <div ref={sentinelRef} style={{ height: 0, width: "100%" }} aria-hidden />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        style={{
          ...pageContainerStyle,
          ...(lockStyle ?? {
            // Pre-measure fallback: fill the white card without growing the shell.
            position: "relative" as const,
            minHeight: 0,
          }),
        }}
      >
        {/* Persistent tab bar: the pad switcher (Agentic ⇄ New Rx Pad) is the
            first item, followed by the section tabs. */}
        <TabsNav />

        {!isAgentixRxEnabled && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              justifyContent: { xs: "flex-start", md: "center" },
              alignItems: "stretch",
              boxSizing: "border-box",
              overflow: { xs: "auto", md: "hidden" },
              WebkitOverflowScrolling: "touch",
              minWidth: 0,
              width: "100%",
              px: { xs: 1, md: 2 },
              py: { xs: 1, md: 1.5 },
              pb: { xs: 1.5, md: 1.5 },
            }}
          >
            <ErrorBoundary fallback={<Box sx={{ p: 2 }}>Unable to load feature access.</Box>}>
              <AgenticRxFeatureAccess
                clinicAccount={
                  prescriberDetails?.address ??
                  prescriberDetails?.response?.[0]?.address
                }
                primaryAdmin={
                  prescriberDetails?.userName ??
                  prescriberDetails?.response?.[0]?.userName
                }
                contactEmail={
                  prescriberDetails?.email ??
                  prescriberDetails?.response?.[0]?.email
                }
              />
            </ErrorBoundary>
          </Box>
        )}

        {isAgentixRxEnabled && !showResults && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              boxSizing: "border-box",
              overflow: { xs: "auto", md: "hidden" },
              px: { xs: 1, sm: 2 },
              py: { xs: 1, sm: 1.5 },
              pb: { xs: 0.5, sm: 1 },
            }}
          >
            <ErrorBoundary fallback={<Box sx={{ p: 2 }}>Unable to load the AgenticRx guide.</Box>}>
              <AgenticRxQuickGuide />
            </ErrorBoundary>
          </Box>
        )}

        {/* Conversation list — sole scrollport */}
        {isAgentixRxEnabled && showResults && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            style={resultsSectionStyle}
          >
            <Box
              ref={resultsContainerRef}
              sx={getResultsContainerSx(primaryColor, barOpen)}
            >
              {conversations.slice().map((conversation, index) => (
                <ConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  isLatest={index === conversations.length - 1}
                  preferredPharmacy={preferredPharmacy}
                  primaryColor={primaryColor}
                  onPharmacySelect={handlePharmacySelection}
                  onFeedback={handleFeedback}
                  onSuggestionSelect={handleSuggestionSelect}
                  onSendPrescription={handleSendPrescription}
                  sendDisabled={isSearching}
                  selectedMedicationPicks={medicationPicksByName}
                  selectedPharmacyPicks={pharmacyPicksByName}
                  onPharmacyLookupSelect={handlePharmacyLookupSelect}
                  onPbmSelect={handlePbmSelect}
                  onFavouriteDrugSelect={handleFavouriteDrugSelect}
                  latestConversationRef={latestConversationRef}
                />
              ))}
            </Box>
          </motion.div>
        )}

        {/* Search Section - NOW AT BOTTOM */}
        {isAgentixRxEnabled && (
          <SearchInputSection
            showResults={showResults}
            voiceSearchCountdown={voiceSearchCountdown}
            onSearch={handleTypedSearch}
            onVoiceResult={handleVoiceResult}
            primaryColor={primaryColor}
            formikRef={formikRef}
            barOpen={barOpen}
            onToggleBar={() => setBarOpen((o) => !o)}
            disabled={isSearching}
            onClearConversation={handleClearConversation}
          />
        )}
      </motion.div>
    </>
  );
}

